"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import {
  resolveCircleActionVariant,
  type CircleActionVariant,
} from "./circle-action-variants";
import { LiquidSurface, type LiquidSurfaceBounceIntensity } from "./liquid-surface";

export interface LiquidButtonListItem {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  trailing?: React.ReactNode;
  variant?: CircleActionVariant;
  disabled?: boolean;
}

export interface LiquidButtonListProps {
  items: LiquidButtonListItem[];
  value?: string | null;
  onChange?: (value: string, item: LiquidButtonListItem) => void;
  orientation?: "horizontal" | "vertical";
  size?: "sm" | "md";
  bounceIntensity?: LiquidSurfaceBounceIntensity;
  className?: string;
  itemClassName?: string;
}

export function LiquidButtonList({
  items,
  value,
  onChange,
  orientation = "horizontal",
  size = "md",
  bounceIntensity = "soft",
  className,
  itemClassName,
}: LiquidButtonListProps) {
  const containerClassName = cn(
    "overflow-visible",
    orientation === "vertical"
      ? "flex flex-col gap-2"
      : "flex flex-wrap gap-2",
    className,
  );

  const baseItemClassName = cn(
    "relative flex min-w-0 items-center gap-3 overflow-hidden text-left transition-transform duration-200 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 focus-visible:ring-offset-1",
    orientation === "vertical"
      ? "w-full"
      : "flex-1 min-w-[200px]",
    size === "sm" ? "px-4 py-3" : "px-4 py-4",
  );

  const labelClassName = size === "sm" ? "text-sm" : "text-[15px]";
  const descriptionClassName =
    size === "sm" ? "text-[11px] leading-snug" : "text-xs leading-relaxed";

  return (
    <div className={containerClassName}>
      {items.map((item) => {
        const isActive = item.value === value;
        const variant = resolveCircleActionVariant(item.variant ?? "slate");

        return (
          <LiquidSurface
            key={item.value}
            as="button"
            type="button"
            variant={variant}
            tone={isActive ? "tinted" : "neutral"}
            shape="sheet"
            size="free"
            interactive={!item.disabled}
            hoverBounce={!item.disabled}
            hoverDeform={!item.disabled}
            bounceIntensity={bounceIntensity}
            deformAxis={orientation === "vertical" ? "both" : "horizontal"}
            shadow={isActive ? "default" : "neutral"}
            reactive={isActive}
            cursorGlow={false}
            disabled={item.disabled}
            aria-label={typeof item.label === "string" ? item.label : undefined}
            onClick={() => !item.disabled && onChange?.(item.value, item)}
            className={cn(
              baseItemClassName,
              isActive
                ? "border border-indigo-200/80 bg-indigo-50/70 text-indigo-950 dark:border-indigo-800/50 dark:bg-indigo-950/35 dark:text-indigo-100"
                : "border border-slate-200/70 bg-white/80 text-slate-700 dark:border-zinc-800/70 dark:bg-zinc-950/50 dark:text-zinc-200",
              itemClassName,
            )}
          >
            <div className="flex min-w-0 flex-1 items-start gap-3">
              {item.icon ? (
                <div
                  className={cn(
                    "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border",
                    isActive
                      ? "border-indigo-200/80 bg-indigo-100/70 text-indigo-600 dark:border-indigo-800/50 dark:bg-indigo-950/50 dark:text-indigo-300"
                      : "border-slate-200/80 bg-slate-50/80 text-slate-500 dark:border-zinc-800/70 dark:bg-zinc-900/70 dark:text-zinc-300",
                  )}
                >
                  {item.icon}
                </div>
              ) : null}

              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <span className={cn("font-semibold tracking-[-0.01em]", labelClassName)}>
                    {item.label}
                  </span>
                  {isActive ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 dark:bg-indigo-300" />
                  ) : null}
                </div>
                {item.description ? (
                  <p className={cn("max-w-full text-slate-500 dark:text-zinc-400", descriptionClassName)}>
                    {item.description}
                  </p>
                ) : null}
              </div>
            </div>

            {item.trailing ? (
              <div className="ml-3 flex shrink-0 items-center">{item.trailing}</div>
            ) : null}
          </LiquidSurface>
        );
      })}
    </div>
  );
}
